import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { toast } from '@/hooks/use-toast';
import { Loader2, Save, RotateCcw } from 'lucide-react';
import { dimensions } from '@/data/enterpriseDiagnosticData';

const SETTINGS_KEY = 'diagnostic_rubrics_override';

export type RubricsOverride = Record<string, Record<number, string>>;
// e.g. { "L1": { 1: "custom criteria", 3: "custom criteria" }, "D2": { 5: "..." } }

export default function DiagnosticRubricsEditor() {
  const qc = useQueryClient();

  const { data: savedRow, isLoading } = useQuery({
    queryKey: ['platform_settings', SETTINGS_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('*')
        .eq('key', SETTINGS_KEY)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const savedOverrides: RubricsOverride | null = savedRow
    ? (savedRow.value as unknown as RubricsOverride)
    : null;

  const [edits, setEdits] = useState<RubricsOverride | null>(null);
  const hasEdits = edits !== null;

  const getDisplayValue = (subId: string, level: number, defaultCriteria: string): string => {
    if (edits?.[subId]?.[level] !== undefined) return edits[subId][level];
    if (savedOverrides?.[subId]?.[level] !== undefined) return savedOverrides[subId][level];
    return defaultCriteria;
  };

  const isOverridden = (subId: string, level: number): boolean => {
    if (edits?.[subId]?.[level] !== undefined) return true;
    return savedOverrides?.[subId]?.[level] !== undefined;
  };

  const handleChange = (subId: string, level: number, value: string) => {
    setEdits(prev => {
      const base = prev || {};
      return {
        ...base,
        [subId]: {
          ...(base[subId] || {}),
          [level]: value,
        },
      };
    });
  };

  const handleRestoreSubDimension = (subId: string) => {
    setEdits(prev => {
      const base = { ...(prev || {}) };
      // Mark all 5 levels as empty string to signal "remove override"
      const defaultSub = dimensions
        .flatMap(d => d.subSections)
        .find(s => s.id === subId);
      if (!defaultSub) return base;
      const restored: Record<number, string> = {};
      defaultSub.rubrics.forEach(r => {
        restored[r.level] = r.criteria;
      });
      base[subId] = restored;
      // Actually we want to remove the override entirely
      delete base[subId];
      // Also remove from saved when we save
      base[`__delete_${subId}`] = {} as any;
      return base;
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Merge edits with saved overrides
      const merged: RubricsOverride = { ...(savedOverrides || {}) };

      if (edits) {
        for (const [key, levels] of Object.entries(edits)) {
          if (key.startsWith('__delete_')) {
            const subId = key.replace('__delete_', '');
            delete merged[subId];
          } else {
            merged[key] = { ...(merged[key] || {}), ...levels };
          }
        }
      }

      // Clean: remove entries that match defaults
      for (const dim of dimensions) {
        for (const sub of dim.subSections) {
          if (merged[sub.id]) {
            for (const rubric of sub.rubrics) {
              if (merged[sub.id][rubric.level] === rubric.criteria) {
                delete merged[sub.id][rubric.level];
              }
            }
            if (Object.keys(merged[sub.id]).length === 0) {
              delete merged[sub.id];
            }
          }
        }
      }

      const { data: existing } = await supabase
        .from('platform_settings')
        .select('id')
        .eq('key', SETTINGS_KEY)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('platform_settings')
          .update({ value: merged as any })
          .eq('key', SETTINGS_KEY);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('platform_settings')
          .insert({ key: SETTINGS_KEY, value: merged as any });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform_settings', SETTINGS_KEY] });
      setEdits(null);
      toast({ title: 'Rúbricas actualizadas correctamente' });
    },
    onError: () => toast({ title: 'Error al guardar rúbricas', variant: 'destructive' }),
  });

  if (isLoading) {
    return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  }

  const totalOverrides = savedOverrides
    ? Object.values(savedOverrides).reduce((acc, levels) => acc + Object.keys(levels).length, 0)
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Rúbricas de Evaluación</CardTitle>
            <p className="text-sm text-muted-foreground">
              Edita los criterios de cada nivel (1-5) para las 15 subdimensiones del marco LIDER-IA.
            </p>
          </div>
          {totalOverrides > 0 && (
            <Badge variant="secondary">{totalOverrides} personalizada{totalOverrides !== 1 ? 's' : ''}</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <Accordion type="single" collapsible>
          {dimensions.map(dim => (
            <AccordionItem key={dim.id} value={dim.id}>
              <AccordionTrigger className="text-sm font-semibold">
                <span className="flex items-center gap-2">
                  <span className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-bold text-primary-foreground" style={{ backgroundColor: dim.color }}>
                    {dim.letter}
                  </span>
                  {dim.fullName}
                  <span className="text-xs text-muted-foreground font-normal">({dim.subSections.length} subdimensiones)</span>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <Accordion type="single" collapsible className="ml-4">
                  {dim.subSections.map(sub => {
                    const hasOverride = sub.rubrics.some(r => isOverridden(sub.id, r.level));
                    return (
                      <AccordionItem key={sub.id} value={sub.id}>
                        <AccordionTrigger className="text-sm">
                          <span className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">{sub.id}</Badge>
                            {sub.title}
                            {hasOverride && <Badge variant="secondary" className="text-[10px] px-1.5">editada</Badge>}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3">
                            {sub.rubrics.map(rubric => {
                              const val = getDisplayValue(sub.id, rubric.level, rubric.criteria);
                              const overridden = isOverridden(sub.id, rubric.level);
                              return (
                                <div key={rubric.level}>
                                  <Label className="flex items-center gap-2 mb-1">
                                    <Badge variant={overridden ? 'default' : 'outline'} className="text-[10px]">
                                      Nivel {rubric.level}
                                    </Badge>
                                    <span className="text-xs text-muted-foreground">{rubric.state}</span>
                                  </Label>
                                  <Textarea
                                    className="text-xs min-h-[60px] font-mono"
                                    value={val}
                                    placeholder={rubric.criteria}
                                    onChange={(e) => handleChange(sub.id, rubric.level, e.target.value)}
                                  />
                                </div>
                              );
                            })}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="gap-1.5 text-xs"
                              onClick={() => handleRestoreSubDimension(sub.id)}
                            >
                              <RotateCcw className="w-3 h-3" /> Restaurar defaults de {sub.id}
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {hasEdits && (
          <div className="flex gap-2 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setEdits(null)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              <Save className="w-4 h-4 mr-1" />
              {saveMutation.isPending ? 'Guardando...' : 'Guardar rúbricas'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
